import { type MutableRefObject, useEffect, useRef } from 'react';

import { DRAG_GHOST_CURSOR_OFFSET } from '~/constants/dnd';

type Position = { x: number; y: number };

export type UseNativeDragTrackingParams = {
  isDragging: boolean;
  clientOffset: Position | null;
  dragItemRef: MutableRefObject<HTMLDivElement | null>;
  offsetRef: MutableRefObject<Position | undefined>;
  grabOffsetRef: MutableRefObject<Position>;
};

/**
 * Tracks native drag position via dragover events for smooth drag preview movement.
 *
 * React DnD's `clientOffset` can stop updating when dragging over elements that call
 * `stopPropagation` on drag events (e.g. ReactFlow canvas). This hook supplements it
 * with native dragover events (captured in the capture phase) so the drag preview
 * keeps following the cursor smoothly.
 */
export const useNativeDragTracking = ({
  isDragging,
  clientOffset,
  dragItemRef,
  offsetRef,
  grabOffsetRef,
}: UseNativeDragTrackingParams) => {
  const nativeMousePositionRef = useRef<Position | undefined>(undefined);
  const clientOffsetRef = useRef<Position | undefined>(undefined);

  useEffect(() => {
    clientOffsetRef.current = clientOffset ?? undefined;
  }, [clientOffset]);

  // Track native drag position via dragover events for smooth movement over canvas/chat
  // (mousemove doesn't fire during drag operations, so we use dragover instead)
  useEffect(() => {
    if (!isDragging) {
      nativeMousePositionRef.current = undefined;
      return;
    }

    if (clientOffsetRef.current !== undefined) {
      nativeMousePositionRef.current = clientOffsetRef.current;
    }

    const handleDragOver = (e: DragEvent) => {
      const position = { x: e.clientX, y: e.clientY };
      nativeMousePositionRef.current = position;

      if (dragItemRef.current !== null) {
        offsetRef.current = position;
        const { x: gx, y: gy } = grabOffsetRef.current;
        dragItemRef.current.style.transform = `translate(${position.x - gx - DRAG_GHOST_CURSOR_OFFSET}px, ${position.y - gy - DRAG_GHOST_CURSOR_OFFSET}px)`;
        dragItemRef.current.style.webkitTransform = `translate(${position.x - gx - DRAG_GHOST_CURSOR_OFFSET}px, ${position.y - gy - DRAG_GHOST_CURSOR_OFFSET}px)`;
      }
    };

    // Listen in capture phase to receive events before canvas handler calls stopPropagation
    window.addEventListener('dragover', handleDragOver, true);
    return () => {
      window.removeEventListener('dragover', handleDragOver, true);
    };
  }, [isDragging, dragItemRef, offsetRef, grabOffsetRef]);

  return { nativeMousePositionRef, clientOffsetRef };
};
